"use client";

import { useAction, useQuery } from "convex/react";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { api } from "../../../convex/_generated/api";
import type { DisplayCurrencyState } from "@/studio/lib/displayCurrency";
import { setDisplayCurrency } from "@/studio/lib/displayCurrency";

const DisplayCurrencyContext = createContext<DisplayCurrencyState>({
  currency: "TTD",
  rail: "wam",
  fxUsdToTtd: null,
});

export function useDisplayCurrency(): DisplayCurrencyState {
  return useContext(DisplayCurrencyContext);
}

export function DisplayCurrencyProvider({ children }: { children: ReactNode }) {
  const rail = useQuery(api.users.getBillingRail);
  const cachedFx = useQuery(api.fx.getUsdToTtd);
  const refreshFx = useAction(api.fx.refreshUsdToTtd);

  useEffect(() => {
    // Blank country is Trinidad, so a USD-only refresh never seeded fxQuotes.
    if (cachedFx && !cachedFx.stale) return;
    void refreshFx({}).catch(() => {});
  }, [cachedFx, refreshFx]);

  const value = useMemo<DisplayCurrencyState>(() => {
    const next: DisplayCurrencyState = {
      currency: rail?.currency ?? "TTD",
      rail: rail?.rail ?? "wam",
      country: rail?.country,
      fxUsdToTtd: cachedFx?.rate ?? rail?.fxUsdToTtd ?? null,
    };
    setDisplayCurrency(next);
    return next;
  }, [rail?.currency, rail?.rail, rail?.country, rail?.fxUsdToTtd, cachedFx?.rate]);

  return (
    <DisplayCurrencyContext.Provider value={value}>
      {children}
    </DisplayCurrencyContext.Provider>
  );
}
